import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Phone, Clock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const Contact = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("send-contact-email", {
        body: formData,
      });

      if (error) throw error;

      toast({
        title: "Melding sendt!",
        description: "Vi tar kontakt med deg så snart som mulig.",
      });
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error sending message:", error);
      toast({
        title: "Kunne ikke sende melding",
        description: "Vennligst prøv igjen senere, eller ring oss på 480 09 271.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="kontakt" aria-labelledby="contact-heading" className="section-padding bg-cream">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Contact Info */}
          <div>
            <p className="text-primary font-sans text-xs tracking-[0.3em] uppercase mb-4">
              Ta kontakt
            </p>
            <h2 id="contact-heading" className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-6">
              Kontakt oss
            </h2>
            <p className="text-muted-foreground font-sans mb-10 max-w-md">
              Vi hjelper deg gjerne med bunad, tilpasning eller spørsmål. 
              Ta kontakt for en uforpliktende prat.
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <MapPin className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-serif text-foreground mb-1">Adresse</h3>
                  <p className="text-muted-foreground font-sans text-sm">
                    Bunadstuggu<br />
                    Kirkegata 38<br />
                    7600 Levanger
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Phone className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-serif text-foreground mb-1">Telefon</h3>
                  <p className="text-muted-foreground font-sans text-sm">
                    480 09 271
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <Clock className="w-5 h-5 text-primary mt-1 flex-shrink-0" />
                <div>
                  <h3 className="font-serif text-foreground mb-1">Åpningstider</h3>
                  <div className="text-muted-foreground font-sans text-sm space-y-1">
                    <p>Mandag – Onsdag: 09.30 – 16.30</p>
                    <p>Torsdag: 09.30 – 18.00</p>
                    <p>Fredag: 09.30 – 16.30</p>
                    <p>Lørdag: 09.30 – 14.00</p>
                    <p className="italic text-muted-foreground/70 pt-2">
                      Utover åpningstid – etter avtale
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-card p-8 lg:p-10 shadow-medium">
            <h3 className="font-serif text-2xl text-foreground mb-6">
              Send oss en melding
            </h3>
            <form onSubmit={handleSubmit} className="space-y-5" aria-label="Kontaktskjema">
              <div>
                <label htmlFor="contact-name" className="sr-only">Ditt navn</label>
                <Input
                  id="contact-name"
                  placeholder="Ditt navn"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="h-12 border-border bg-background font-sans"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="sr-only">E-postadresse</label>
                <Input
                  id="contact-email"
                  type="email"
                  placeholder="E-postadresse"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-12 border-border bg-background font-sans"
                />
              </div>
              <div>
                <label htmlFor="contact-phone" className="sr-only">Telefonnummer</label>
                <Input
                  id="contact-phone"
                  type="tel"
                  placeholder="Telefonnummer"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="h-12 border-border bg-background font-sans"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="sr-only">Din melding</label>
                <Textarea
                  id="contact-message"
                  placeholder="Din melding"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={5}
                  className="border-border bg-background font-sans resize-none"
                />
              </div>
              <Button type="submit" variant="hero" size="lg" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? "Sender..." : "Send melding"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
